import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, AlertCircle, Lock, Loader2 } from "lucide-react";
import api from "../lib/api";
import NationalityAutocomplete from "../components/NationalityAutocomplete";

/**
 * Public custom-registration form (iter-68).
 *
 * Consumed via `/inscription/{slug}`. Decrements seats server-side. When the
 * quota is reached the endpoint returns 403 with the verbatim closed-message
 * and the UI switches to a dedicated "Inscriptions closes" screen.
 *
 * Payment flows:
 *  - free / configurable → immediate pass + QR + confirmation email
 *  - paid (prix fixe)    → server returns {requires_payment, payment_url}
 *                          and we redirect to FineoPay
 */
const BBR_LOGO =
  "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/movphq5f_AA-02.png";

export default function CorporateRegistration() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [closedMessage, setClosedMessage] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    surname: "",
    email: "",
    phone: "",
    whatsapp: "",
    nationality: "",
    payment_method: "cash",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/corporate-form/${token}`);
      setMeta(data);
      setError(null);
      setClosedMessage(null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const status = e.response?.status;
      if (status === 403 && typeof detail === "string" && detail.includes("closes")) {
        // Quota reached — surface the exact server message
        setClosedMessage(detail);
        setMeta(null);
        setError(null);
      } else {
        setError(detail || "Lien invalide ou expiré");
        setMeta(null);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, [token]);

  // iter-92 · Duplicate-registration modal state (backend returns HTTP 409
  // with { code: "duplicate_registration", existing, message }).
  const [duplicate, setDuplicate] = useState(null);

  const doPost = async (payload) => {
    try {
      const { data } = await api.post(`/corporate-form/${token}/register`, payload);
      if (data.requires_payment && data.payment_url) {
        setRedirecting(true);
        toast.success("Redirection vers le paiement…");
        setTimeout(() => { window.location.href = data.payment_url; }, 900);
        return { ok: true };
      }
      setSubmitted(data);
      toast.success("Inscription confirmée");
      return { ok: true };
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 409 && detail?.code === "duplicate_registration") {
        setDuplicate({ existing: detail.existing, payload });
        return { ok: false, duplicate: true };
      }
      if (status === 403 && typeof detail === "string" && detail.includes("closes")) {
        setClosedMessage(detail);
        setMeta(null);
      } else {
        const msg = (detail && typeof detail === "object") ? (detail.message || "Erreur") : (detail || "Erreur");
        toast.error(msg);
      }
      return { ok: false };
    }
  };

  const submit = async () => {
    const required = ["name", "surname", "email", "phone", "nationality"];
    for (const k of required) {
      if (!form[k]?.trim()) {
        toast.error(`Champ requis : ${k}`);
        return;
      }
    }
    setSubmitting(true);
    const payload = {
      kind: "client",
      name: form.name.trim(),
      surname: form.surname.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim(),
      whatsapp: (form.whatsapp || form.phone).trim(),
      nationality: form.nationality.trim(),
    };
    const paymentMode = meta.payment_mode;
    const isFixedPaid = paymentMode === "paid" && meta.price_mode === "fixed";
    if (paymentMode === "configurable" || (paymentMode === "paid" && meta.price_mode === "per_offer")) {
      payload.payment_method = form.payment_method;
    } else if (isFixedPaid) {
      payload.payment_method = "card";
    }
    await doPost(payload);
    setSubmitting(false);
  };

  const confirmReplaceDuplicate = async () => {
    if (!duplicate) return;
    setSubmitting(true);
    const payload = { ...duplicate.payload, force_replace: true };
    setDuplicate(null);
    await doPost(payload);
    setSubmitting(false);
  };
  const cancelDuplicate = () => setDuplicate(null);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[#0A0A0A]/55">Chargement…</div>;
  }

  // ── Closed screen — exact server-side verbiage ──────────────────────
  if (closedMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7] px-6">
        <div
          className="max-w-md w-full bg-white border border-[#0A0A0A]/8 p-10 text-center"
          data-testid="corporate-form-closed"
        >
          <Lock size={30} className="mx-auto text-[#B8922A] mb-4" />
          <h1 className="font-display-serif text-2xl text-[#0A0A0A] mb-3">Inscriptions closes</h1>
          <p className="text-sm text-[#0A0A0A]/70 leading-relaxed" data-testid="closed-message">
            {closedMessage}
          </p>
        </div>
      </div>
    );
  }

  if (error || !meta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7] px-6">
        <div className="max-w-md w-full bg-white border border-[#0A0A0A]/8 p-10 text-center" data-testid="corporate-form-error">
          <Lock size={28} className="mx-auto text-[#B8922A] mb-4" />
          <h1 className="font-display-serif text-2xl text-[#0A0A0A] mb-3">Lien indisponible</h1>
          <p className="text-sm text-[#0A0A0A]/65">{error}</p>
        </div>
      </div>
    );
  }

  // ── Redirection screen (paid mode) ──────────────────────────────────
  if (redirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7] px-6">
        <div className="max-w-md w-full bg-white border border-[#0A0A0A]/8 p-10 text-center" data-testid="corporate-form-redirect">
          <Loader2 className="mx-auto animate-spin text-[#B8922A] mb-4" size={30} />
          <h1 className="font-display-serif text-2xl text-[#0A0A0A] mb-2">Redirection…</h1>
          <p className="text-sm text-[#0A0A0A]/65">
            Vous allez être redirigé vers la page de paiement sécurisée.
          </p>
        </div>
      </div>
    );
  }

  // ── Success screen (free/immediate mode) ────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7] px-6 py-10">
        <div className="max-w-md w-full bg-white border border-[#0A0A0A]/8 p-8 text-center" data-testid="corporate-form-success">
          <Check size={32} className="mx-auto text-green-600 mb-4" />
          <h1 className="font-display-serif text-2xl text-[#0A0A0A] mb-2">Inscription confirmée</h1>
          <p className="text-sm text-[#0A0A0A]/65 mb-5">
            Votre place pour <b>{meta.group_name}</b> est réservée.
          </p>
          {submitted.ticket_image && (
            <div className="mb-5">
              <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#B8922A] mb-2">
                Votre pass d&apos;embarquement
              </div>
              <img
                src={submitted.ticket_image}
                alt="Pass d'embarquement"
                className="w-full border border-[#0A0A0A]/10"
                data-testid="corp-ticket-image"
              />
              <a
                href={submitted.ticket_image}
                download={`bbr-pass-${submitted.ref_code || submitted.qr_token.slice(0, 8)}.png`}
                className="inline-block mt-3 px-4 py-2 bg-[#0A0A0A] text-white text-[0.65rem] uppercase tracking-[0.22em] hover:bg-[#B8922A] transition-colors"
                data-testid="corp-ticket-download"
              >
                Télécharger le pass
              </a>
            </div>
          )}
          <div className="bg-[#FAFAF7] border border-[#0A0A0A]/8 p-3 text-left text-[0.72rem] text-[#0A0A0A]/65 leading-relaxed">
            <div className="text-[0.55rem] uppercase tracking-[0.22em] text-[#B8922A] mb-1">Référence</div>
            <code className="text-[#0A0A0A] block break-all text-[0.78rem] font-medium">{submitted.ref_code || submitted.qr_token}</code>
            <div className="mt-2 text-[0.7rem]">
              Places restantes : <b>{submitted.remaining_seats}</b> / {meta.max_participants}
            </div>
          </div>
          <p className="text-[0.7rem] text-[#0A0A0A]/50 mt-4">
            Conservez ce pass et présentez le QR à l&apos;embarquement.
          </p>
        </div>
      </div>
    );
  }

  const paymentMode = meta.payment_mode;
  const isFixedPaid = paymentMode === "paid" && meta.price_mode === "fixed";
  const showPaymentMethodSelect = paymentMode === "configurable"
    || (paymentMode === "paid" && meta.price_mode === "per_offer");
  const dateLine = [
    meta.event_date,
    meta.event_time,
  ].filter(Boolean).join(" · ");

  return (
    <div className="min-h-screen bg-[#FAFAF7] py-10 px-4" data-testid="corporate-form-page">
      {/* iter-92 · Duplicate registration confirmation modal */}
      {duplicate && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          data-testid="duplicate-dialog"
        >
          <div className="bg-white max-w-md w-full p-6 sm:p-8 border border-[#B8922A]/30">
            <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#B8922A] mb-3">
              · Enregistrement récent ·
            </div>
            <h2 className="font-display-serif text-2xl text-[#0A0A0A] mb-4">
              Un enregistrement existe déjà (&lt; 1 heure)
            </h2>
            <p className="text-sm text-[#0A0A0A]/70 leading-relaxed mb-5">
              Un enregistrement existe déjà pour cette personne depuis moins d&apos;une heure
              {duplicate.existing?.first_name ? (
                <> — <strong className="text-[#0A0A0A]">
                  {duplicate.existing.first_name} {duplicate.existing.last_name || ""}
                </strong></>
              ) : null}
              .<br /><br />
              Souhaitez-vous <strong>supprimer cet enregistrement</strong> et
              en créer un nouveau ?
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={confirmReplaceDuplicate}
                disabled={submitting}
                className="flex-1 bg-[#B8922A] text-white px-4 py-3 text-[0.7rem] uppercase tracking-[0.22em] hover:bg-[#9d7a23] transition-colors disabled:opacity-60"
                data-testid="duplicate-confirm-btn"
              >
                {submitting ? "Traitement…" : "Oui, remplacer"}
              </button>
              <button
                type="button"
                onClick={cancelDuplicate}
                disabled={submitting}
                className="flex-1 border border-[#0A0A0A]/25 text-[#0A0A0A] px-4 py-3 text-[0.7rem] uppercase tracking-[0.22em] hover:border-[#B8922A] hover:text-[#B8922A] transition-colors"
                data-testid="duplicate-cancel-btn"
              >
                Non, annuler
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-xl mx-auto">
        {/* Cover image banner */}
        {meta.cover_image && (
          <div className="w-full aspect-[16/6] bg-[#0A0A0A] overflow-hidden">
            <img
              src={meta.cover_image}
              alt={meta.group_name}
              className="w-full h-full object-cover"
              data-testid="corp-cover-image"
            />
          </div>
        )}

        {/* Header — event info on top */}
        <div className="bg-[#0A0A0A] text-white p-8 text-center">
          {!meta.cover_image && <img src={BBR_LOGO} alt="BBR" className="h-14 mx-auto mb-4 opacity-95" />}
          <div className="text-[0.62rem] uppercase tracking-[0.32em] text-[#E5D9C0] mb-2">
            Inscription
          </div>
          <h1 className="font-display-serif text-3xl tracking-tight" data-testid="corporate-form-title">
            {meta.group_name}
          </h1>
          <div className="mt-2 text-[0.85rem] text-white/75">
            {meta.reservation_type}
            {dateLine ? ` · ${dateLine}` : ""}
          </div>
          {meta.description && (
            <p className="mt-3 text-[0.85rem] text-white/70 leading-relaxed max-w-md mx-auto" data-testid="corp-description">
              {meta.description}
            </p>
          )}
          <div className="mt-5 inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] text-[#E5D9C0]/80">
            <AlertCircle size={11} />
            {meta.remaining_seats} place{meta.remaining_seats > 1 ? "s" : ""} restante{meta.remaining_seats > 1 ? "s" : ""} sur {meta.max_participants}
          </div>
          {isFixedPaid && meta.price_amount > 0 && (
            <div className="mt-3 inline-flex items-center gap-2 text-[0.9rem] text-[#E5D9C0] font-medium" data-testid="corp-fixed-price">
              Tarif : {meta.price_amount.toLocaleString("fr-FR")} FCFA / personne
            </div>
          )}
        </div>

        {/* Form */}
        <div className="bg-white border border-[#0A0A0A]/8 border-t-0 p-7 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              ["surname", "Nom *", "text"],
              ["name", "Prénom *", "text"],
              ["email", "Email *", "email"],
              ["phone", "Téléphone *", "tel"],
              ["whatsapp", "WhatsApp", "tel"],
            ].map(([key, label, type]) => (
              <div key={key} className={key === "whatsapp" ? "sm:col-span-2" : ""}>
                <label className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0A0A0A]/55 block mb-1">{label}</label>
                <input
                  type={type}
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  placeholder={key === "whatsapp" ? "Optionnel — par défaut = téléphone" : ""}
                  className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
                  data-testid={`corp-${key}`}
                />
              </div>
            ))}
            <div className="sm:col-span-2">
              <label className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0A0A0A]/55 block mb-1">Nationalité *</label>
              <NationalityAutocomplete
                value={form.nationality}
                onChange={(e) => setForm({ ...form, nationality: typeof e === "string" ? e : e?.target?.value || "" })}
                lang="fr"
                testId="corp-nationality"
              />
            </div>
          </div>

          {showPaymentMethodSelect && (
            <div>
              <label className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0A0A0A]/55 block mb-1">Mode de paiement *</label>
              <select
                value={form.payment_method}
                onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
                data-testid="corp-payment-method"
              >
                <option value="cash">Espèces (à l&apos;arrivée)</option>
                <option value="card">Carte bancaire</option>
                <option value="mobile_money">Mobile Money</option>
              </select>
            </div>
          )}

          <button
            onClick={submit}
            disabled={submitting}
            className="w-full bg-[#0A0A0A] text-white py-3 text-[0.7rem] uppercase tracking-[0.28em] hover:bg-[#B8922A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            data-testid="corp-submit"
          >
            {submitting ? "Inscription…" : isFixedPaid ? "Continuer vers le paiement" : "S'inscrire"}
          </button>
        </div>
      </div>
    </div>
  );
}
